/**
 * `<AppRoot>` — the provider stack + router (frontend.md §2.1, §5 step 1).
 *
 * The component hierarchy (post-local-embed pivot 2026-05-17):
 *   <QueryClientProvider>   — server-cache state (@tanstack/query)
 *   └── <ThemeProvider>     — applies the useUiStore theme to <html>
 *       └── <BrowserRouter> — react-router data-router; routes mirror the surfaces
 *           └── <AppShell>  — the shell + the ShellMount contract for Agent 9
 *       └── <ToastHost>     — the toast queue (frontend.md §4.2)
 *
 * `<CapabilityProvider>` (D-004 local-hardware tier detection) was removed when the
 * embedding pipeline pivoted from local ONNX inference to OpenAI's hosted API — the
 * frontend no longer has a "tier" to derive render budgets from, and the boot splash
 * no longer needs to block on a capability probe.
 *
 * The loopback-token provider from frontend.md §2.1 (`<AuthTokenProvider>`) is folded
 * into the REST client itself (`src/api/client.ts` reads
 * `window.__ALETHEIA__.loopbackToken` directly) rather than a React provider — the
 * token is process-global, injected by the Tauri shell, and never changes at runtime.
 */

import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { useRef } from 'react';
import { ClerkProvider } from '@clerk/clerk-react';
import { createQueryClient, ThemeProvider } from '@/providers';
import { buildPersistOptions } from '@/providers/queryPersist';
import { AppShell } from '@/shell';
import { ToastHost } from '@/components';
import { DesktopUpdateWatcher } from '@/desktop/DesktopUpdateWatcher';
import { SignInPage } from '@/surfaces/auth/SignInPage';
import { SignUpPage } from '@/surfaces/auth/SignUpPage';
import { RequireAuth } from '@/surfaces/auth/RequireAuth';

/**
 * VITE_CLERK_PUBLISHABLE_KEY is injected by Vite at build time. The publishable
 * key is *safe* in the browser bundle — Clerk's threat model assumes it's
 * exposed; the secret key (server-side only) is what carries authority. The
 * deploy-frontend-swa.yml workflow reads it from a GitHub Actions secret and
 * passes it to `vite build` so the value lands in the bundle.
 *
 * If unset (local dev without a .env), Clerk's provider would throw on mount.
 * We gracefully fall back to a console warning so the editor still loads for
 * non-auth-related dev work.
 */
const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;

export function AppRoot(): JSX.Element {
  // One QueryClient for the app lifetime — stable identity across renders.
  const queryClientRef = useRef(createQueryClient());
  // Persist the cache to IndexedDB so a reload paints last-seen notes + the notebook tree
  // instantly from disk, then revalidates in the background (Phase 3 perf, 2026-06-10).
  const persistOptionsRef = useRef(buildPersistOptions());

  // Defensive fallback: if Clerk isn't configured (no env var), still render
  // the app but with auth disabled — useful for local-only dev and avoids
  // crashing the boot when an unconfigured deploy goes out.
  if (!CLERK_PUBLISHABLE_KEY) {
    // eslint-disable-next-line no-console
    console.warn(
      '[AppRoot] VITE_CLERK_PUBLISHABLE_KEY not set — Clerk disabled. Set it in your .env or GitHub Actions secrets.',
    );
    return (
      <PersistQueryClientProvider
        client={queryClientRef.current}
        persistOptions={persistOptionsRef.current}
      >
        <ThemeProvider>
          <BrowserRouter>
            <AppShell />
          </BrowserRouter>
          <ToastHost />
          <DesktopUpdateWatcher />
        </ThemeProvider>
      </PersistQueryClientProvider>
    );
  }

  return (
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      // After signin / signup, land on the editor. Marketing CTAs encode a
      // ?redirect_url=... which Clerk honors automatically and takes priority.
      signInFallbackRedirectUrl="/editor"
      signUpFallbackRedirectUrl="/editor"
      // Tell Clerk where our auth pages live so its <SignIn>/<SignUp> components
      // and the redirect-on-unauthenticated logic link to the right routes.
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
    >
      <PersistQueryClientProvider
        client={queryClientRef.current}
        persistOptions={persistOptionsRef.current}
      >
        <ThemeProvider>
          <BrowserRouter>
            {/*
              Auth pages render as SIBLINGS of <AppShell>, not nested inside it,
              so the shell chrome (NavSidebar / GlobalStatusBar / CommandPalette)
              doesn't show on /sign-in or /sign-up. The auth surface owns the
              full viewport — Forest-dark backdrop + brand wordmark + Clerk form
              with no app chrome around it.

              Everything else (the * route) lands in <AppShell>, which has its
              own inner <Routes> via <RouteOutlet> to render the active surface
              (editor / search / chat / settings).
            */}
            <Routes>
              <Route path="/sign-in/*" element={<SignInPage />} />
              <Route path="/sign-up/*" element={<SignUpPage />} />
              {/*
                Every non-auth route is gated by <RequireAuth>. Unsigned users
                get redirected to /sign-in with redirect_url=<original path> so
                Clerk lands them back where they were after signing in. This
                closes the bug where the desktop app and the web SPA loaded
                /editor with notes visible to anyone.
              */}
              <Route
                path="/*"
                element={
                  <RequireAuth>
                    <AppShell />
                  </RequireAuth>
                }
              />
            </Routes>
          </BrowserRouter>
          <ToastHost />
          <DesktopUpdateWatcher />
        </ThemeProvider>
      </PersistQueryClientProvider>
    </ClerkProvider>
  );
}
